/**
 * New estimate screen — the entry point of the estimate builder.
 *
 * Pushed OVER the tabs on the parent (app) stack (title "New Estimate", with back).
 * Picks a customer (required), optional context / markup% / tax rate, then POSTs
 * createEstimate and router.replace()s to the builder at /(app)/estimates/[id] so
 * the freshly-created draft is the back-target instead of this form.
 *
 * Both staff and owner may CREATE a draft estimate (binding approval is owner-only,
 * enforced on the detail screen + server — CLAUDE.md invariant 6).
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1). No raw style values.
 */

import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createEstimate,
  listCustomers,
  listTaxRates,
} from '../../../src/api/estimates';
import {
  Screen,
  Text,
  Card,
  Button,
  Field,
  Select,
  ListRow,
} from '../../../src/components/ui';
import { theme } from '../../../src/theme';
import { extractErrorMessage, humanize } from '../../../src/lib/format';
import type {
  CreateEstimateRequest,
  EstimateContext,
} from '@beam/api-types';
import { CoachmarkOverlay } from '../../../src/components/help';
import { FLOW_KEYS } from '../../../src/help';

const CONTEXT_OPTIONS: ReadonlyArray<{ value: EstimateContext; label: string }> = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'government', label: 'Government' },
];

export default function NewEstimateScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();

  const [customerId, setCustomerId] = useState<string | null>(null);
  const [context, setContext] = useState<EstimateContext | null>(null);
  const [markup, setMarkup] = useState('');
  const [taxRateId, setTaxRateId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const customersQuery = useQuery({
    queryKey: ['customers'],
    queryFn: listCustomers,
    staleTime: 60_000,
  });
  const taxRatesQuery = useQuery({
    queryKey: ['tax-rates'],
    queryFn: listTaxRates,
    staleTime: 60_000,
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateEstimateRequest) => createEstimate(body),
    onSuccess: (estimate) => {
      void queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.setQueryData(['estimates', estimate.id], estimate);
      router.replace(`/(app)/estimates/${estimate.id}`);
    },
    onError: (err) => setFormError(extractErrorMessage(err, 'Could not create estimate.')),
  });

  const onCreate = () => {
    setFormError(null);
    if (!customerId) {
      setFormError('Select a customer to start the estimate.');
      return;
    }
    const body: CreateEstimateRequest = { customerId };
    if (context) body.context = context;
    if (taxRateId) body.taxRateId = taxRateId;
    const markupNum = parseFloat(markup);
    if (markup.trim() && !Number.isNaN(markupNum)) body.markupPct = markupNum;
    createMutation.mutate(body);
  };

  const customers = customersQuery.data ?? [];
  const taxRates = (taxRatesQuery.data ?? []).filter((t) => t.isActive);
  const activeTax = taxRates.find((t) => t.id === taxRateId) ?? null;

  return (
    <Screen scroll>
      <CoachmarkOverlay flowKey={FLOW_KEYS.buildEstimate} />
      <Text variant="title">New estimate</Text>

      {/* Customer picker */}
      <View style={styles.section}>
        <Text variant="label" color="secondary">
          Customer
        </Text>
        {customersQuery.isLoading ? (
          <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading customers" />
        ) : customersQuery.isError ? (
          <Card>
            <Text variant="body" color="danger">
              {extractErrorMessage(customersQuery.error, 'Failed to load customers.')}
            </Text>
            <Button
              title="Retry"
              variant="secondary"
              size="sm"
              fullWidth={false}
              onPress={() => customersQuery.refetch()}
            />
          </Card>
        ) : customers.length === 0 ? (
          <Card>
            <Text variant="body" color="secondary">
              No customers yet. Add your first customer, then come back to build the estimate.
            </Text>
            <Button
              title="+ Add a customer"
              fullWidth={false}
              onPress={() => router.push('/(app)/customers')}
              accessibilityLabel="Add a customer"
            />
          </Card>
        ) : (
          <View style={styles.list}>
            <Button
              title="+ New customer"
              variant="secondary"
              size="sm"
              fullWidth={false}
              onPress={() => router.push('/(app)/customers')}
              accessibilityLabel="Add a customer"
            />
            {customers.map((c) => (
              <ListRow
                key={c.id}
                title={c.displayName}
                subtitle={c.companyName ?? humanize(c.customerType)}
                right={
                  customerId === c.id ? (
                    <Text variant="label" color="link">
                      Selected
                    </Text>
                  ) : undefined
                }
                onPress={() => setCustomerId(c.id)}
                style={customerId === c.id ? styles.selectedRow : undefined}
              />
            ))}
          </View>
        )}
      </View>

      {/* Optional context */}
      <Select
        label="Context (optional)"
        options={CONTEXT_OPTIONS}
        value={context}
        onChange={setContext}
      />

      {/* Optional markup */}
      <Field
        label="Markup % (optional)"
        placeholder="e.g. 20"
        value={markup}
        onChangeText={setMarkup}
        keyboardType="decimal-pad"
        accessibilityLabel="Markup percent"
      />

      {/* Optional tax rate */}
      {taxRates.length > 0 ? (
        <Select
          label="Tax rate (optional)"
          options={taxRates.map((t) => ({ value: t.id, label: `${t.name} ${t.ratePct}%` }))}
          value={taxRateId}
          onChange={setTaxRateId}
        />
      ) : null}
      {activeTax ? (
        <Text variant="small" color="secondary">
          Tax: {activeTax.name} ({activeTax.ratePct}%)
        </Text>
      ) : null}

      {formError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {formError}
          </Text>
        </Card>
      ) : null}

      <Text variant="small" color="secondary">
        Next, you'll add services and materials that prefill priced, editable lines. No blank
        line-by-line entry required.
      </Text>

      <Button
        title="Create estimate"
        onPress={onCreate}
        loading={createMutation.isPending}
        disabled={createMutation.isPending || !customerId}
        accessibilityLabel="Create estimate"
      />
    </Screen>
  );
}

const styles = StyleSheet.create({
  section: { gap: theme.spacing.sm },
  list: { gap: theme.spacing.sm },
  selectedRow: { borderColor: theme.color.action.primary },
});
